import React from 'react';

// Card + subcomponents. Mirrors react-bootstrap's Card.* API.
type DivProps = React.HTMLAttributes<HTMLDivElement>;

interface CardType extends React.FC<DivProps> {
  Header: React.FC<DivProps>;
  Body: React.FC<DivProps>;
  Footer: React.FC<DivProps>;
  Title: React.FC<React.HTMLAttributes<HTMLHeadingElement> & { as?: React.ElementType }>;
  Subtitle: React.FC<React.HTMLAttributes<HTMLHeadingElement> & { as?: React.ElementType }>;
  Text: React.FC<React.HTMLAttributes<HTMLParagraphElement> & { as?: React.ElementType }>;
}

export const Card = (({ className = '', children, ...rest }: DivProps) => (
  <div className={`tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-shadow-card ${className}`} {...rest}>
    {children}
  </div>
)) as CardType;

// data-slot replaces Bootstrap's .card-header as the stable hook for "this is the
// header". Tailwind classes are styling and churn with design tweaks, so tests and
// any future querying anchor on the slot instead.
Card.Header = ({ className = '', children, ...rest }) => (
  <div data-slot="card-header" className={`tw-border-b tw-border-line tw-bg-surface tw-px-4 tw-py-3 ${className}`} {...rest}>{children}</div>
);
Card.Body = ({ className = '', children, ...rest }) => (
  <div className={`tw-p-4 ${className}`} {...rest}>{children}</div>
);
Card.Footer = ({ className = '', children, ...rest }) => (
  <div className={`tw-border-t tw-border-line tw-bg-surface-alt tw-px-4 tw-py-3 ${className}`} {...rest}>{children}</div>
);
Card.Title = ({ as, className = '', children, ...rest }) => {
  const Comp: React.ElementType = as || 'h5';
  return <Comp className={`tw-mb-1 tw-text-lg tw-font-semibold tw-text-content ${className}`} {...rest}>{children}</Comp>;
};
Card.Subtitle = ({ as, className = '', children, ...rest }) => {
  const Comp: React.ElementType = as || 'h6';
  return <Comp className={`tw-mb-2 tw-text-sm tw-text-content-muted ${className}`} {...rest}>{children}</Comp>;
};
Card.Text = ({ as, className = '', children, ...rest }) => {
  const Comp: React.ElementType = as || 'p';
  return <Comp className={`tw-mb-2 ${className}`} {...rest}>{children}</Comp>;
};

export default Card;
